import api from './api';
import { toQueryDateYMD } from '../utils/formatters';
import { DocumentosKpis, normalizeDocumentosKpis } from '../utils/documentosKpis';
import { umDesdeDetalleTransaccion } from '../utils/umVentaDocumento';
import { normalizeUmSnapshotFacFromApi } from '../utils/umDocumentoDisplay';

export interface NotaCreditoVentaDetalle {
    nroIdformulario: number;
    nroItem: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioUnitario?: number;
    descuento?: number;
    porcDesc?: number;
    subtotal?: number;
    notas?: string;
    um?: string;
    /** Snapshot Fac: cantidad ingresada por el usuario. */
    cantidadUmfac?: number;
    /** Snapshot Fac: UM ingresada. */
    umfac?: string;
    /** Snapshot Fac: factor hacia UM base. */
    equivUmfac?: number;
    /** Snapshot Fac: precio unitario ingresado (en umfac). */
    precioUnitarioUmfac?: number;
    codigoMoneda?: number;
    cotizacion?: number;
}

export interface NotaCreditoVenta {
    nroFormulario: number;
    idCompania?: string;
    nroTipoFormulario: string;
    tipoCliente: string;
    fechaEmision: Date | string;
    horaEmision?: Date | string;
    nroCliente: number;
    nombre: string;
    direccionCliente?: string | null;
    direccionEnvio?: string | null;
    nroVendedor?: number;
    nroLista?: string;
    codigoMoneda: string | number;
    importeTotal: number;
    asentado?: string;
    descripcion?: string;
    usuario?: string;
    detalles?: NotaCreditoVentaDetalle[];
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
    facturado?: string;
    nroFormularioFactura?: number;
    integrar?: number;
    estado?: string;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
    cuit?: string;
    caeNro?: string;
    caeVencimiento?: string | null;
    /** Asiento de libro diario vinculado al comprobante, si existe. */
    nroAsiento?: number | null;
}

export interface AcreditacionOrdenLinea {
    nroItem: number;
    codigoArticulo?: string;
    descripcion?: string;
    cantidad: number;
    cantidadAcreditable: number;
    nroFormularioNotaCredito?: number;
}

export interface AcreditacionOrdenPreview {
    nroFormularioOrden: number;
    nroFormularioRemito?: number;
    nroFormularioFactura?: number;
    puedeAcreditar: boolean;
    motivo?: string;
    notasCreditoVigentes?: number[];
    lineas?: AcreditacionOrdenLinea[];
}

export interface OrdenDesdeNotaCandidato {
    nroFormularioRemito: number;
    nroItemRemito: number;
    pendiente: number;
}

export interface OrdenDesdeNotaLinea {
    nroItem: number;
    codigoArticulo?: string;
    descripcion?: string;
    cantidadNota: number;
    cantidadLibre: number;
    cantidadSugerida: number;
    nroFormularioRemito?: number;
    nroItemRemito?: number;
    candidatos?: OrdenDesdeNotaCandidato[];
}

export interface OrdenDesdeNotaPreview {
    nroFormularioNota: number;
    nroFormularioFactura?: number;
    nroUbicacionSugerida?: number;
    puedeGenerar: boolean;
    motivo?: string;
    lineas?: OrdenDesdeNotaLinea[];
}

export interface NotaCreditoVentaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date | string;
    fechaHasta?: Date | string;
    nroCliente?: string;
    nroVendedor?: string;
    asentado?: string;
    estado?: string;
    nroSuc?: number;
}

export interface NotaCreditoVentaPagedResult {
    items: NotaCreditoVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

/** Item para crear una Nota de Crédito (POST api/NotasCreditoVenta) */
export interface NotaCreditoItemCreateDto {
    codigoArticulo: string;
    descripcion?: string;
    cantidad: number;
    precioUnitario: number;
    /** Porcentaje de descuento del renglón (0-100). precioUnitario = precio de lista. */
    porcDesc?: number;
    /** Seguimiento: formulario de la línea origen (factura, pedido, etc.). */
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    um?: string;
    codigoMoneda?: number;
    cotizacion?: number;
    notas?: string;
    lotes?: import('./loteService').LoteAsignacion[];
    lotesComponentes?: { codigoArticulo: string; lotes: import('./loteService').LoteAsignacion[] }[];
}

/** Formulario encabezado para crear una Nota de Crédito */
export interface NotaCreditoFormularioCreateDto {
    idCompania: string;
    descripcion?: string;
    nroSuc?: number;
    fechaEmision?: string;
    fechaVencim?: string;
    nroCliente: number;
    nombre?: string;
    nroLista?: string;
    formaDePago?: string;
    nroUbicacionOrigen?: number;
    ordenDeCompra?: string;
    codigoDireccion?: string;
    codigoDireccionEnvio?: string;
    formaEntrega?: string;
    nroFormularioFactura?: number;
    /** Si se informa y es > 0, se usa en el comprobante; si no, el backend toma el vendedor del cliente. */
    nroVendedor?: number;
    /** CUIT o documento del receptor en el comprobante. */
    cuit?: string;
    /** Condición IVA del receptor (I, M, C, …). */
    tipoIva?: string;
}

export interface NotaCreditoCreateOptions {
    emitirConCaePrimero?: boolean;
    guardarSinCae?: boolean;
}

export interface NotaCreditoCreateResult {
    notaCredito?: NotaCreditoVenta;
    requiereCae?: boolean;
    caeObtenido?: boolean;
    notaCreditoGrabada?: boolean;
    caeError?: string;
    caeObs?: string;
    caeNro?: string;
    caeVencimiento?: string | null;
    nroComprobante?: number;
}

/** Payload para POST api/NotasCreditoVenta */
export interface NotaCreditoCreateDto {
    formulario: NotaCreditoFormularioCreateDto;
    items: NotaCreditoItemCreateDto[];
    emitirConCaePrimero?: boolean;
    guardarSinCae?: boolean;
}

const notaCreditoVentaService = {
    getNotasCreditoVenta: async (params: NotaCreditoVentaSearchParams): Promise<NotaCreditoVentaPagedResult> => {
        const queryParams: Record<string, string | number> = {
            pageNumber: params.pageNumber,
            pageSize: params.pageSize,
        };
        if (params.searchTerm) queryParams.searchTerm = params.searchTerm;
        if (params.fechaDesde) queryParams.fechaDesde = toQueryDateYMD(params.fechaDesde);
        if (params.fechaHasta) queryParams.fechaHasta = toQueryDateYMD(params.fechaHasta);
        if (params.nroCliente) queryParams.nroCliente = params.nroCliente;
        if (params.nroVendedor) queryParams.nroVendedor = params.nroVendedor;
        if (params.asentado) queryParams.asentado = params.asentado;
        if (params.estado) queryParams.estado = params.estado;
        if (params.nroSuc != null && params.nroSuc > 0) queryParams.NroSuc = params.nroSuc;

        const response = await api.get<NotaCreditoVentaPagedResult>('/api/NotasCreditoVenta', { params: queryParams });
        return response.data;
    },

    getNotasCreditoVentaKpis: async (
        params: Omit<NotaCreditoVentaSearchParams, 'pageNumber' | 'pageSize'>,
    ): Promise<DocumentosKpis> => {
        const queryParams: Record<string, unknown> = {
            pageNumber: 1,
            pageSize: 1,
        };
        if (params.searchTerm) queryParams.searchTerm = params.searchTerm;
        if (params.fechaDesde) queryParams.fechaDesde = toQueryDateYMD(params.fechaDesde);
        if (params.fechaHasta) queryParams.fechaHasta = toQueryDateYMD(params.fechaHasta);
        if (params.nroCliente) queryParams.nroCliente = params.nroCliente;
        if (params.nroVendedor) queryParams.nroVendedor = params.nroVendedor;
        if (params.asentado) queryParams.asentado = params.asentado;
        if (params.estado) queryParams.estado = params.estado;
        if (params.nroSuc != null && params.nroSuc > 0) queryParams.NroSuc = params.nroSuc;
        const response = await api.get('/api/NotasCreditoVenta/kpis', {
            params: queryParams,
        });
        return normalizeDocumentosKpis(response.data);
    },

    getNotaCreditoVenta: async (id: number): Promise<NotaCreditoVenta> => {
        const response = await api.get<NotaCreditoVenta>(`/api/NotasCreditoVenta/${id}`);
        const data = response.data;
        return {
            ...data,
            detalles: (data.detalles || []).map((d) => ({
                ...d,
                um: umDesdeDetalleTransaccion(d) || undefined,
                ...normalizeUmSnapshotFacFromApi(d),
            })),
        };
    },

    getNotaCreditoVentaPDF: async (id: number, idCompania?: string): Promise<Blob> => {
        const params = idCompania?.trim()
            ? `?idCompania=${encodeURIComponent(idCompania.trim())}`
            : '';
        const response = await api.get(`/api/NotasCreditoVenta/${id}/pdf${params}`, {
            responseType: 'blob'
        });
        return response.data;
    },

    obtenerCaeArgentina: async (nroFormulario: number, idCompania?: string): Promise<NotaCreditoVenta> => {
        const params = idCompania?.trim()
            ? `?idCompania=${encodeURIComponent(idCompania.trim())}`
            : '';
        const response = await api.post<NotaCreditoVenta>(
            `/api/NotasCreditoVenta/${nroFormulario}/obtener-cae-argentina${params}`
        );
        return response.data;
    },

    createNotaCredito: async (
        payload: NotaCreditoCreateDto,
        options?: NotaCreditoCreateOptions,
    ): Promise<NotaCreditoVenta> => {
        const body: NotaCreditoCreateDto = {
            ...payload,
            emitirConCaePrimero: options?.emitirConCaePrimero === true,
            guardarSinCae: options?.guardarSinCae === true,
        };
        const response = await api.post<NotaCreditoVenta | NotaCreditoCreateResult>(
            '/api/NotasCreditoVenta',
            body,
        );
        const data = response.data as NotaCreditoCreateResult & NotaCreditoVenta;
        if (data && typeof data === 'object' && data.notaCredito != null) {
            return data.notaCredito;
        }
        return data as NotaCreditoVenta;
    },

    anularNotaCredito: async (id: number): Promise<void> => {
        await api.post(`/api/NotasCreditoVenta/${id}/anular`);
    },

    /** Envía la nota de crédito al email del cliente, con el PDF adjunto. */
    enviarNotaCreditoPorEmailAlCliente: async (
        id: number
    ): Promise<{ enviado: boolean; mensaje?: string }> => {
        const response = await api.post<{ enviado: boolean; mensaje?: string }>(
            `/api/NotasCreditoVenta/${id}/enviar-email-cliente`
        );
        return response.data;
    },

    getAcreditacionOrden: async (ordenId: number): Promise<AcreditacionOrdenPreview> => {
        const response = await api.get<AcreditacionOrdenPreview>(`/api/NotasCreditoVenta/desde-orden/${ordenId}`);
        return response.data;
    },

    acreditarOrden: async (ordenId: number): Promise<NotaCreditoVenta> => {
        const response = await api.post<NotaCreditoVenta>(`/api/NotasCreditoVenta/desde-orden/${ordenId}`);
        return response.data;
    },

    getParaOrdenDevolucion: async (id: number): Promise<OrdenDesdeNotaPreview> => {
        const response = await api.get<OrdenDesdeNotaPreview>(`/api/NotasCreditoVenta/${id}/para-orden-devolucion`);
        return response.data;
    },

    /** Solo SUPERADMIN: borra asientos vinculados al formulario y crea uno nuevo con la lógica actual. */
    regenerarAsientoNotaCreditoVenta: async (
        id: number
    ): Promise<{ nroAsiento: number; message?: string }> => {
        const response = await api.post<{ nroAsiento: number; message?: string }>(
            `/api/NotasCreditoVenta/${id}/regenerar-asiento`
        );
        return response.data;
    },
};

export default notaCreditoVentaService;

